// Access Assets Application
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { SystemOptionsProvider } from '@/contexts/SystemOptionsContext';
import { LanguageSettingsProvider } from '@/contexts/LanguageSettingsContext';
import { PermissionsProvider } from '@/providers/PermissionsProvider';
import { RightPanelProvider } from '@/contexts/RightPanelContext';
import { PageRefreshProvider } from '@/contexts/PageRefreshContext';
import { TourProvider } from '@/contexts/TourContext';
import { AssetDetailSheetProvider } from '@/hooks/use-asset-detail-sheet';
import { ImportAssetsModalProvider } from '@/contexts/ImportAssetsModalContext';
import { ImportAssetsModal } from '@/components/import/import-assets-modal';
import { AIAssistantProvider } from '@/contexts/ai-assistant-context';
import DashboardNew from '@/pages/dashboard-new';
import AssetsPage from '@/pages/assets';
import AssetDetail from '@/pages/assets/AssetDetail';
import AdditionsPage from '@/pages/transactions/additions';
import DepreciationPage from '@/pages/transactions/depreciation';
import RevaluationPage from '@/pages/transactions/revaluation';
import DisposalsPage from '@/pages/transactions/disposals';
import InsightsPage from '@/pages/insights';
import Enquiry from '@/pages/enquiry';
import { MaintenanceLog } from '@/pages/maintenance-log';
import HelpPage from '@/pages/help';
import InsuranceValueReport from '@/pages/reports/insurance-value';
import StatutoryReport from '@/pages/reports/statutory-report';
import LocationsPage from '@/pages/settings/locations';
import LocationDetail from '@/pages/settings/location-detail';
import CategoriesPage from '@/pages/settings/categories';
import CategoryDetailPage from '@/pages/administration/category-detail';
import EquipmentPage from '@/pages/settings/equipment';
import EquipmentDetailPage from '@/pages/settings/equipment-detail';
import DisposalReasonsPage from '@/pages/settings/disposal-reasons';
import AssetOptionsPage from '@/pages/settings/asset-options';
import FeaturesSettingsPage from '@/pages/settings/features';
import IntegrationLogPage from '@/pages/settings/integration-log';
import PeriodManagementPage from '@/pages/settings/period-management';
import CompliancePage from '@/pages/compliance';
import ComplianceAssetsPage from '@/pages/compliance-assets';
import ComplianceRegulationsPage from '@/pages/compliance-regulations';
import ComplianceSchedulePage from '@/pages/compliance-schedule';
import AuditPage from '@/pages/audit';
import CheckInOutDashboard from '@/pages/checkinout/dashboard';
import CheckInOutCalendar from '@/pages/checkinout/calendar';
import CheckInOutSettingsPage from '@/pages/checkinout/settings';
import CheckOutAsset from '@/pages/checkinout/checkout';
// Asset Import Module
import ImportMethodSelection from '@/pages/assets/import';
import CSVImport from '@/pages/assets/import/csv';
import AccountingSync from '@/pages/assets/import/accounting-sync';
import TransactionScan from '@/pages/assets/import/transaction-scan';

// New Audit Management Module (Redesign)
import ActiveAudits from '@/pages/audits/ActiveAudits';
import ScheduledAudits from '@/pages/audits/ScheduledAudits';
import AuditHistory from '@/pages/audits/AuditHistory';
import { ExceptionsRedesigned as ExceptionsPage } from '@/pages/audits/ExceptionsRedesigned';
import AuditDetail from '@/pages/audits/AuditDetail';
import CreateAudit from '@/pages/audits/CreateAudit';
import ExceptionDetail from '@/pages/ExceptionDetail';
import TasksHome from '@/pages/tasks/TasksHome';
import TaskDetail from '@/pages/tasks/TaskDetail';
import TeamsPermissionsPage from '@/pages/administration/teams-permissions';
import { Toaster } from '@/components/ui/toaster';
import { NavigationTracker } from '@/components/NavigationTracker';
import { WootricProvider } from '@/providers/WootricProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { PermissionGuard } from '@/components/auth/PermissionGuard';

function WootricWrapper({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
  const { user: wootricUser } = useUserProfile();
  return <WootricProvider user={wootricUser}>{children}</WootricProvider>;
}

function AppContent({ bypassPermissions }: Readonly<{ bypassPermissions?: boolean }>) {
  return (
    <SystemOptionsProvider>
      <LanguageSettingsProvider>
        <PermissionsProvider bypassPermissions={bypassPermissions}>
          <PageRefreshProvider>
            <TourProvider>
              <AssetDetailSheetProvider>
                <ImportAssetsModalProvider>
                  <AIAssistantProvider>
                    <BrowserRouter>
                      <NavigationTracker />
                      <Routes>
                        <Route path="/" element={<AppLayout />}>
                          <Route index element={<Navigate to="/dashboard" replace />} />
                          {/* Dashboard is always accessible - it's the fallback for unauthorized access */}
                          <Route path="dashboard" element={<DashboardNew />} />

                          {/* Assets - protected by 'assets' permission */}
                          <Route path="assets" element={
                            <PermissionGuard screenKey="assets" showAccessDenied>
                              <AssetsPage />
                            </PermissionGuard>
                          } />
                          <Route path="assets/:assetId" element={
                            <PermissionGuard screenKey="assets" showAccessDenied>
                              <AssetDetail />
                            </PermissionGuard>
                          } />
                          <Route path="assets/import" element={
                            <PermissionGuard screenKey="import-assets" showAccessDenied>
                              <ImportMethodSelection />
                            </PermissionGuard>
                          } />
                          <Route path="assets/import/csv" element={
                            <PermissionGuard screenKey="import-assets" showAccessDenied>
                              <CSVImport />
                            </PermissionGuard>
                          } />
                          <Route path="assets/import/accounting-sync" element={
                            <PermissionGuard screenKey="import-assets" showAccessDenied>
                              <AccountingSync />
                            </PermissionGuard>
                          } />
                          <Route path="assets/import/transaction-scan" element={
                            <PermissionGuard screenKey="import-assets" showAccessDenied>
                              <TransactionScan />
                            </PermissionGuard>
                          } />

                          {/* Transactions - protected by respective permissions */}
                          <Route path="transactions">
                            <Route path="additions" element={
                              <PermissionGuard screenKey="additions" showAccessDenied>
                                <AdditionsPage />
                              </PermissionGuard>
                            } />
                            <Route path="depreciation" element={
                              <PermissionGuard screenKey="depreciation" showAccessDenied>
                                <DepreciationPage />
                              </PermissionGuard>
                            } />
                            <Route path="revaluation" element={
                              <PermissionGuard screenKey="revaluation" showAccessDenied>
                                <RevaluationPage />
                              </PermissionGuard>
                            } />
                            <Route path="disposals" element={
                              <PermissionGuard screenKey="disposals" showAccessDenied>
                                <DisposalsPage />
                              </PermissionGuard>
                            } />
                          </Route>

                          {/* Reports - protected by respective permissions */}
                          <Route path="insights" element={
                            <PermissionGuard screenKey="insights" showAccessDenied>
                              <InsightsPage />
                            </PermissionGuard>
                          } />
                          <Route path="enquiry" element={
                            <PermissionGuard screenKey="enquiry" showAccessDenied>
                              <Enquiry />
                            </PermissionGuard>
                          } />
                          <Route path="maintenance-log" element={<MaintenanceLog />} />
                          <Route path="reports">
                            <Route path="insurance-value" element={
                              <PermissionGuard screenKey="insights" showAccessDenied>
                                <InsuranceValueReport />
                              </PermissionGuard>
                            } />
                            <Route path="statutory" element={
                              <PermissionGuard screenKey="insights" showAccessDenied>
                                <StatutoryReport />
                              </PermissionGuard>
                            } />
                          </Route>
                          <Route path="audit" element={
                            <PermissionGuard screenKey="audit-trail" showAccessDenied>
                              <AuditPage />
                            </PermissionGuard>
                          } />

                          {/* Compliance - protected by 'compliance' permission */}
                          <Route path="compliance" element={
                            <PermissionGuard screenKey="compliance" showAccessDenied>
                              <CompliancePage />
                            </PermissionGuard>
                          } />
                          <Route path="compliance/assets" element={
                            <PermissionGuard screenKey="compliance" showAccessDenied>
                              <ComplianceAssetsPage />
                            </PermissionGuard>
                          } />
                          <Route path="compliance/regulations" element={
                            <PermissionGuard screenKey="compliance" showAccessDenied>
                              <ComplianceRegulationsPage />
                            </PermissionGuard>
                          } />
                          <Route path="compliance/schedule" element={
                            <PermissionGuard screenKey="compliance" showAccessDenied>
                              <ComplianceSchedulePage />
                            </PermissionGuard>
                          } />

                          {/* Tasks Module - protected by 'tasks' permission */}
                          <Route path="tasks">
                            <Route index element={
                              <PermissionGuard screenKey="tasks" showAccessDenied>
                                <TasksHome />
                              </PermissionGuard>
                            } />
                            <Route path="home" element={
                              <PermissionGuard screenKey="tasks" showAccessDenied>
                                <TasksHome />
                              </PermissionGuard>
                            } />
                            <Route path=":taskId" element={
                              <PermissionGuard screenKey="tasks" showAccessDenied>
                                <TaskDetail />
                              </PermissionGuard>
                            } />
                          </Route>

                          {/* Check-In/Out - protected by 'checkinout' permission */}
                          <Route path="checkinout">
                            <Route index element={
                              <PermissionGuard screenKey="checkinout" showAccessDenied>
                                <CheckInOutDashboard />
                              </PermissionGuard>
                            } />
                            <Route path="calendar" element={
                              <PermissionGuard screenKey="checkinout" showAccessDenied>
                                <CheckInOutCalendar />
                              </PermissionGuard>
                            } />
                            <Route path="checkout" element={
                              <PermissionGuard screenKey="checkinout" showAccessDenied>
                                <CheckOutAsset />
                              </PermissionGuard>
                            } />
                            <Route path="settings" element={
                              <PermissionGuard screenKey="checkinout" showAccessDenied>
                                <CheckInOutSettingsPage />
                              </PermissionGuard>
                            } />
                          </Route>

                          {/* Audit Management Module Routes - protected by 'audits-manager' permission */}
                          <Route path="audits">
                            <Route index element={<Navigate to="/audits/active" replace />} />
                            <Route path="dashboard" element={<Navigate to="/audits/active" replace />} />
                            <Route path="active" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <ActiveAudits />
                              </PermissionGuard>
                            } />
                            <Route path="scheduled" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <ScheduledAudits />
                              </PermissionGuard>
                            } />
                            <Route path="history" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <AuditHistory />
                              </PermissionGuard>
                            } />
                            <Route path="exceptions" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <ExceptionsPage />
                              </PermissionGuard>
                            } />
                            <Route path="exceptions/:id" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <ExceptionDetail />
                              </PermissionGuard>
                            } />
                            <Route path="create" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <CreateAudit />
                              </PermissionGuard>
                            } />
                            <Route path=":id" element={
                              <PermissionGuard screenKey="audits-manager" showAccessDenied>
                                <AuditDetail />
                              </PermissionGuard>
                            } />
                          </Route>

                          {/* Administration - protected by respective permissions */}
                          <Route path="administration">
                            <Route path="teams-permissions" element={
                              <PermissionGuard screenKey="team-settings" showAccessDenied>
                                <TeamsPermissionsPage />
                              </PermissionGuard>
                            } />
                            <Route path="imports" element={<Navigate to="/assets/import" replace />} />
                            <Route path="locations" element={
                              <PermissionGuard screenKey="locations" showAccessDenied>
                                <LocationsPage />
                              </PermissionGuard>
                            } />
                            <Route path="locations/:locationId" element={
                              <PermissionGuard screenKey="locations" showAccessDenied>
                                <LocationDetail />
                              </PermissionGuard>
                            } />
                            <Route path="categories" element={
                              <PermissionGuard screenKey="categories" showAccessDenied>
                                <CategoriesPage />
                              </PermissionGuard>
                            } />
                            <Route path="categories/:id" element={
                              <PermissionGuard screenKey="categories" showAccessDenied>
                                <CategoryDetailPage />
                              </PermissionGuard>
                            } />
                            <Route path="equipment" element={
                              <PermissionGuard screenKey="equipment" showAccessDenied>
                                <EquipmentPage />
                              </PermissionGuard>
                            } />
                            <Route path="equipment/:id" element={
                              <PermissionGuard screenKey="equipment" showAccessDenied>
                                <EquipmentDetailPage />
                              </PermissionGuard>
                            } />
                            <Route path="disposal-reasons" element={
                              <PermissionGuard screenKey="disposal-reasons" showAccessDenied>
                                <DisposalReasonsPage />
                              </PermissionGuard>
                            } />
                          </Route>

                          {/* Settings - protected by respective permissions */}
                          <Route path="settings">
                            <Route path="features" element={
                              <PermissionGuard screenKey="feature-settings" showAccessDenied>
                                <FeaturesSettingsPage />
                              </PermissionGuard>
                            } />
                            <Route path="asset-options" element={
                              <PermissionGuard screenKey="asset-options" showAccessDenied>
                                <AssetOptionsPage />
                              </PermissionGuard>
                            } />
                            <Route path="integration-log" element={
                              <PermissionGuard screenKey="feature-settings" showAccessDenied>
                                <IntegrationLogPage />
                              </PermissionGuard>
                            } />
                            <Route path="period-management" element={
                              <PermissionGuard screenKey="feature-settings" showAccessDenied>
                                <PeriodManagementPage />
                              </PermissionGuard>
                            } />
                          </Route>

                          {/* Help - protected by 'help' permission */}
                          <Route path="help" element={
                            <PermissionGuard screenKey="help" showAccessDenied>
                              <HelpPage />
                            </PermissionGuard>
                          } />
                        </Route>
                      </Routes>
                      <Toaster />
                      <ImportAssetsModal />
                    </BrowserRouter>
                  </AIAssistantProvider>
                </ImportAssetsModalProvider>
              </AssetDetailSheetProvider>
            </TourProvider>
          </PageRefreshProvider>
        </PermissionsProvider>
      </LanguageSettingsProvider>
    </SystemOptionsProvider>
  );
}

export default function App({ bypassPermissions }: Readonly<{ bypassPermissions?: boolean }> = {}): React.ReactElement {
  return (
    <RightPanelProvider>
      <WootricWrapper>
        <AppContent bypassPermissions={bypassPermissions} />
      </WootricWrapper>
    </RightPanelProvider>
  );
}
